import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  TrendingUp,
  Sun,
  Smile,
  Info,
  MessageSquare,
  Check,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { StageNav, type StageKey } from "@/components/marketing/StageNav";

const TOKENS = {
  ivory: "#FBF7F4",
  sand: "#FFE3E8",
  charcoal: "#1A0A24",
  white: "#FFFFFF",
  muted: "#3F2F4A",
  accent: "#B91C5C",
  accentSoft: "#FBE4EE",
};

const SERIF = "var(--font-display)";
const SERIF_ED = "var(--font-serif)";

export const Route = createFileRoute("/perimenopause")({
  head: () => ({
    meta: [
      { title: "Perimenopause — KnowMeno" },
      {
        name: "description",
        content:
          "Perimenopause: the years leading up to menopause — hormone shifts, symptoms, and what to expect.",
      },
      { property: "og:title", content: "Perimenopause — KnowMeno" },
      {
        property: "og:description",
        content:
          "Stage 1 of 3. The transition years — typically beginning in your 40s.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://knowmeno.com/perimenopause" },
    ],
    links: [
      { rel: "canonical", href: "https://knowmeno.com/perimenopause" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        headline: "Perimenopause — KnowMeno",
        description: "Perimenopause: the years leading up to menopause — hormone shifts, symptoms, and what to expect.",
        url: "https://knowmeno.com/perimenopause",
        author: {
          "@type": "Organization",
          name: "KnowMeno",
        },
      }),
    }],
  }),
  component: PerimenopausePage,
});

function CountUp({ value, suffix = "" }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.textContent = `${value}${suffix}`;
      return;
    }
    let start: number | null = null;
    const dur = 1100;
    let raf = 0;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = `${Math.round(eased * value)}${suffix}`;
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, suffix]);
  return <span ref={ref}>0{suffix}</span>;
}




function PerimenopausePage() {
  const tiles = [
    { k: "4–8 years", l: "Typical length of the perimenopause transition" },
    { k: "Mid-40s", l: "Most women notice the first changes here" },
    { k: "Irregular cycles", l: "Periods get shorter, longer, or skip months" },
    { k: "Fluctuating", l: "Estrogen swings up and down — not a steady decline" },
  ];

  const symptoms = [
    "Irregular periods",
    "Hot flashes",
    "Night sweats",
    "Sleep changes",
    "Mood swings",
    "Brain fog",
    "Anxiety",
    "Heavier or lighter bleeding",
    "Breast tenderness",
    "Lower libido",
  ];

  const checkIns = [
    "Very heavy bleeding, or bleeding between periods",
    "Cycles shorter than 21 days or longer than 35 days that persist",
    "Symptoms that disrupt your sleep, work, or relationships",
    "Symptoms starting before age 40 — worth investigating early",
  ];

  return (
    <div style={{ background: TOKENS.ivory, color: TOKENS.charcoal }}>
      <SiteHeader />

      <div className="mx-auto max-w-6xl px-5 pt-6 sm:px-8">
        <p
          className="text-[12px] uppercase tracking-[0.22em]"
          style={{ color: TOKENS.muted }}
        >
          Understanding menopause · Stage 1 of 3
        </p>
        <div className="mt-6">
          <StageNav current={"peri" satisfies StageKey} />
        </div>
      </div>

      {/* Hero */}
      <section className="px-5 pt-12 pb-10 sm:px-8 sm:pt-16 sm:pb-14">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: TOKENS.accent }}
            >
              Stage 01 of 3 · usually begins in your 40s
            </p>
            <h1
              className="mt-4"
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(44px, 8vw, 86px)",
                lineHeight: 1.02,
                letterSpacing: "-0.025em",
                color: TOKENS.charcoal,
              }}
            >
              Perimenopause
            </h1>
            <p
              className="mt-3"
              style={{
                fontFamily: SERIF_ED,
                fontStyle: "italic",
                fontSize: "clamp(22px, 3.4vw, 34px)",
                color: TOKENS.accent,
              }}
            >
              The transition years.
            </p>
            <p
              className="mt-6 max-w-2xl text-[17px] leading-relaxed"
              style={{ color: TOKENS.muted }}
            >
              Perimenopause is the stretch of years leading up to menopause. Your
              ovaries gradually make less estrogen, but the change isn't smooth —
              hormones swing up and down. Cycles become unpredictable and symptoms
              come and go. For most women this transition lasts four to eight years
              and ends 12 months after the final period.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Quick facts */}
      <section className="px-5 py-16 sm:px-8 sm:py-20" style={{ background: TOKENS.sand + "55" }}>
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="mb-8 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: TOKENS.accent }}
            >
              <TrendingUp className="h-4 w-4" aria-hidden /> The quick facts
            </p>
          </ScrollReveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <ScrollReveal>
              <div className="flex flex-col gap-5">
                <div
                  className="km-lift flex items-end gap-5 rounded-3xl p-7"
                  style={{
                    background: TOKENS.accentSoft,
                    border: `1px solid ${TOKENS.accent}33`,
                  }}
                >
                  <div>
                    <div
                      className="text-[13px] font-semibold"
                      style={{ color: TOKENS.accent, opacity: 0.85 }}
                    >
                      average start
                    </div>
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontSize: "clamp(56px, 11vw, 88px)",
                        lineHeight: 0.88,
                        letterSpacing: "-0.03em",
                        color: TOKENS.accent,
                      }}
                    >
                      <CountUp value={47} />
                    </div>
                  </div>
                  <p
                    className="mb-2 max-w-[210px] text-[14px] leading-snug"
                    style={{ color: TOKENS.muted }}
                  >
                    Most women begin perimenopause in their mid-40s — though it can
                    start as early as the late 30s.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {tiles.map((t) => (
                    <div
                      key={t.k}
                      className="km-lift rounded-2xl p-4"
                      style={{
                        background: TOKENS.white,
                        border: `1px solid ${TOKENS.sand}aa`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: SERIF,
                          fontSize: 18,
                          color: TOKENS.accent,
                          lineHeight: 1.15,
                        }}
                      >
                        {t.k}
                      </div>
                      <div
                        className="mt-1 text-[13px] leading-snug"
                        style={{ color: TOKENS.muted }}
                      >
                        {t.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delayMs={120}>
              <div
                className="km-lift h-full rounded-3xl p-7"
                style={{
                  background: TOKENS.white,
                  border: `1px solid ${TOKENS.sand}aa`,
                }}
              >
                <p
                  className="mb-4 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em]"
                  style={{ color: TOKENS.accent }}
                >
                  <Sun className="h-4 w-4" aria-hidden /> What's happening in your body
                </p>
                <p className="text-[15px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  Estrogen doesn't fall in a straight line — it{" "}
                  <strong style={{ color: TOKENS.charcoal }}>swings</strong> as
                  ovulation becomes irregular. Those swings drive many of the
                  symptoms women notice first.
                </p>
                <div
                  className="mt-6 border-t pt-4"
                  style={{ borderColor: `${TOKENS.sand}aa`, borderTopStyle: "dashed" }}
                >
                  <div
                    className="mb-2 flex justify-between text-[11.5px] font-semibold"
                    style={{ color: TOKENS.muted, letterSpacing: "0.04em" }}
                  >
                    <span>Estrogen over time</span>
                    <span>swinging, then falling</span>
                  </div>
                  <svg viewBox="0 0 240 80" className="block h-auto w-full">
                    <line
                      x1="0"
                      y1="40"
                      x2="240"
                      y2="40"
                      stroke={`${TOKENS.sand}`}
                      strokeWidth="1"
                      strokeDasharray="3 4"
                    />
                    <path
                      d="M0,40 C12,18 24,62 36,30 C48,8 60,58 72,28 C86,6 100,60 116,32 C132,8 148,58 168,46 C190,38 214,58 240,66"
                      fill="none"
                      stroke={TOKENS.accent}
                      strokeWidth="2.6"
                      strokeLinecap="round"
                    />
                    <circle cx="116" cy="32" r="4.5" fill={TOKENS.white} stroke={TOKENS.accent} strokeWidth="2.4" />
                  </svg>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="px-5 py-16 sm:px-8 sm:py-20" style={{ background: TOKENS.ivory }}>
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="mb-5 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: TOKENS.accent }}
            >
              <Smile className="h-4 w-4" aria-hidden /> Symptoms you might notice
            </p>
            <p
              className="mb-7 max-w-2xl text-[16px] leading-relaxed"
              style={{ color: TOKENS.muted }}
            >
              Symptoms come and go and{" "}
              <strong style={{ color: TOKENS.charcoal }}>change over time</strong>.
              No two women have the same experience — and your own experience can
              shift from month to month.
            </p>
            <div className="flex flex-wrap gap-2.5">
              {symptoms.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13.5px]"
                  style={{
                    background: TOKENS.accentSoft,
                    color: TOKENS.charcoal,
                    border: `1px solid ${TOKENS.accent}28`,
                  }}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: TOKENS.accent }}
                    aria-hidden
                  />
                  {s}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Good to know */}
      <section className="px-5 pb-4 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <div
              className="km-lift flex gap-4 rounded-2xl p-5"
              style={{
                background: TOKENS.white,
                border: `1px solid ${TOKENS.sand}aa`,
                borderLeft: `4px solid ${TOKENS.accent}`,
              }}
            >
              <div
                className="grid h-10 w-10 flex-none place-items-center rounded-xl"
                style={{ background: TOKENS.accentSoft, color: TOKENS.accent }}
                aria-hidden
              >
                <Info className="h-5 w-5" />
              </div>
              <div>
                <p
                  className="mb-1 text-[12px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: TOKENS.accent }}
                >
                  Good to know
                </p>
                <p className="text-[15px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  Pregnancy is still possible during perimenopause. Until you've
                  gone 12 months with no period, contraception may still be needed
                  if you want to avoid pregnancy.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* When to check in */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <div
              className="km-lift rounded-3xl p-7"
              style={{
                background: TOKENS.white,
                border: `1px solid ${TOKENS.sand}aa`,
              }}
            >
              <p
                className="mb-5 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: TOKENS.accent }}
              >
                <MessageSquare className="h-4 w-4" aria-hidden /> When to check in with
                your clinician
              </p>
              <ul className="grid gap-3">
                {checkIns.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-[15px]" style={{ color: TOKENS.muted }}>
                    <Check
                      className="mt-0.5 h-5 w-5 flex-none"
                      style={{ color: TOKENS.accent }}
                      aria-hidden
                    />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <p
                className="mt-5 text-[12.5px]"
                style={{ color: TOKENS.muted, opacity: 0.85 }}
              >
                General guidance only — your clinician can tell you what's right for your
                situation.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Next */}
      <section className="px-5 pb-16 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:justify-end">
          <Link
            to="/menopause"
            className="km-lift flex flex-col rounded-2xl border px-5 py-3 text-left transition sm:text-right"
            style={{
              background: TOKENS.white,
              borderColor: `${TOKENS.sand}aa`,
              minWidth: 220,
            }}
          >
            <span
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] sm:justify-end"
              style={{ color: TOKENS.muted }}
            >
              Next stage <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </span>
            <span
              style={{
                fontFamily: SERIF,
                fontSize: 18,
                color: TOKENS.charcoal,
              }}
            >
              Menopause
            </span>
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <footer className="px-5 pb-16 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <div
            className="flex items-start gap-4 border-t pt-6"
            style={{ borderColor: `${TOKENS.sand}aa` }}
          >
            <ShieldCheck
              className="h-7 w-7 flex-none"
              style={{ color: TOKENS.accent }}
              aria-hidden
            />
            <div>
              <p className="text-[13.5px] leading-relaxed" style={{ color: TOKENS.muted }}>
                <strong style={{ color: TOKENS.charcoal }}>
                  These are educational insights to help you understand patterns — not
                  medical advice, diagnosis, or treatment.
                </strong>{" "}
                Every woman's experience is different. Talk to your clinician about
                what's right for you.
              </p>
              <p
                className="mt-2 text-[12px]"
                style={{ color: TOKENS.muted, opacity: 0.75 }}
              >
                Figures reflect general population data from NIH MedlinePlus, Cleveland
                Clinic, StatPearls, and the Study of Women's Health Across the Nation
                (SWAN).
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
